import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { IIndeksRetailFacetOutput } from '$models';
import {
    IHelloRetailFilterSelections,
    IHelloRetailSelectedFacet,
    IHelloRetailSortingOption,
    IMappedHelloRetailProduct,
} from '~/modules/search/interfaces/suggestion';
import { FilterControlTypes, defaultHelloRetailSortOption } from '../../helpers';
import { ProductGrid } from './product-grid';
import { HelloRetailFacetControls } from './hello-retail-facet-controls';
import { Text } from '$components/elements/text';
import { useTranslation } from '$hooks';

type Props = {
    productResult: IMappedHelloRetailProduct[] | undefined;
    onLoadNextPage: () => void;
    onSelectedFacets: (facets: IHelloRetailFilterSelections) => void;
    loadingPage?: boolean;
    total?: number;
    facets?: IIndeksRetailFacetOutput[];
    sortingOptions?: IHelloRetailSortingOption[];
    userInteracted?: boolean;
};

export const HelloRetailProductList: FC<Props> = ({
    productResult,
    onLoadNextPage,
    loadingPage,
    onSelectedFacets,
    total,
    facets,
    sortingOptions,
    userInteracted,
}: Props) => {
    const { translate } = useTranslation();

    const selectedFacets = useMemo(() => {
        const selected: IHelloRetailFilterSelections = {};
        const selectedFacets: IHelloRetailSelectedFacet[] = [];
        facets?.forEach((facet) => {
            if (facet.control === FilterControlTypes.Slider && facet.isSelected) {
                if (facet.currentMin !== undefined && facet.currentMax !== undefined) {
                    selectedFacets.push({
                        key: `${facet.key}`,
                        query: `${facet.key}:${facet.currentMin},${facet.currentMax}`,
                        title: facet.name,
                    });
                }
                return;
            }
            const selectedFacetList = facet.facetResults?.filter((fr) => fr.isSelected);
            if (selectedFacetList) {
                selectedFacetList.forEach((selectedFacet) => {
                    if (selectedFacet.query?.name && selectedFacet.query?.value) {
                        selectedFacets.push({
                            key: selectedFacet.query.value,
                            query: selectedFacet.query.value,
                            title: selectedFacet.query.name,
                        });
                    }
                });
            }
        });
        if (selectedFacets.length > 0) {
            selected.selectedFacets = selectedFacets;
        }
        return selected;
    }, [facets]);

    const sortingOptionsWithDefault: IHelloRetailSortingOption[] = useMemo(() => {
        const optionSelected = sortingOptions?.some((option) => option.selected);
        return [
            { ...defaultHelloRetailSortOption, selected: optionSelected ? false : true },
            ...(sortingOptions || []),
        ];
    }, [sortingOptions]);

    if (!productResult?.length && !selectedFacets.selectedFacets?.length) {
        return null;
    }

    return (
        <FilterModuleContainer>
            <HelloRetailFacetControls
                onConfirm={onSelectedFacets}
                onSelectFacet={onSelectedFacets}
                totalResults={total}
                facets={facets}
                selectedFacets={selectedFacets}
                sortingOptions={sortingOptionsWithDefault}
            />

            <ProductGrid
                products={productResult}
                totalResults={total}
                onLoadMore={onLoadNextPage}
                loadingPage={loadingPage}
                userInteracted={userInteracted}
            />
            {!productResult?.length && (
                <NoProductMessage>
                    <Text dimmed>{translate('search.searchResultPage.noProductsToShow')}</Text>
                </NoProductMessage>
            )}
        </FilterModuleContainer>
    );
};

const FilterModuleContainer = styled.div({});

const NoProductMessage = styled.div({});
